import React, { useState} from "react";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import { Input } from "@components/input";
import Wrapper from "./addressField.styles";
import { getAddressErrorValues, getAddressTouchedValues } from "./shared";
import { useOpenAddressOnErrors } from "./useOpenAddressOnErrors";
import { ExpandedAddressDetails } from "./expandedAddressDetails";
import { useSelector } from "react-redux";
import { PaperWidthRow } from "@components/paper/paper";
import { PaddingTopMobile } from "@components/padding";
import { PaddingLeftCol } from "@components/leftGrid/PaddingLeftCol";
import FieldWell from "@components/fieldWell";

const pendingFieldsSelector = state => state.loanForm.pendingFields;

export const AddressField = ({...props}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue
  } = props;
  const { unitNumber, streetNumber, suburb, street, state, postCode } = values;
  const { unitNumber: pendingUnitNumber, streetNumber: pendingStreetNumber, 
    suburb: pendingSuburb, street: pendingStreet, state: pendingState, 
    postCode: pendingPostCode } = useSelector(pendingFieldsSelector);
  
  const shouldShowAddressFields = unitNumber || pendingUnitNumber || streetNumber || 
    pendingStreetNumber || suburb || pendingSuburb || street || pendingStreet || 
    state || pendingState || postCode || pendingPostCode;
  const [address, setAddress] = useState("");
  const [manual, toggleManual] = useState(shouldShowAddressFields);
  const handleChangeLocal = newAddress => {
    setAddress(newAddress);
    if (newAddress.length === 0) {
      toggleManual(false);
      resetAddress();
    }
  };

  const resetAddress = () => {
    setFieldValue("unitNumber", "");
    setFieldValue("streetNumber", "");
    setFieldValue("street", "");
    setFieldValue("state", "");
    setFieldValue("suburb", "");
    setFieldValue("postCode", "");
  }

  const handleSelect = newAddress => {
    resetAddress();
    setAddress(newAddress);
    geocodeByAddress(newAddress)
      .then(results => {
        const add = results[0];
        const unitNumberIndex = (
          add.address_components || []
        ).findIndex(item => item.types.includes("subpremise"));
        const streetNumberIndex = (
          add.address_components || []
        ).findIndex(item => item.types.includes("street_number"));
        const stateIndex = (add.address_components || []).findIndex(item =>
          item.types.includes("administrative_area_level_1")
        );
        const streetIndex = (add.address_components || []).findIndex(item =>
          item.types.includes("route")
        );
        const suburIndex = (add.address_components || []).findIndex(item =>
          item.types.includes("locality")
        );
        const postalCodeIndex = (add.address_components || []).findIndex(item =>
          item.types.includes("postal_code")
        );
        toggleManual(true);
        unitNumberIndex !== -1 &&
          setFieldValue(
            "unitNumber",
            add.address_components[unitNumberIndex].short_name.replace('Unit ', '')
          );
        streetNumberIndex !== -1 &&
          setFieldValue(
            "streetNumber",
            add.address_components[streetNumberIndex].short_name
          );
        streetIndex !== -1 &&
          setFieldValue(
            "street",
            add.address_components[streetIndex].long_name
          );
        stateIndex !== -1 &&
          setFieldValue("state", add.address_components[stateIndex].short_name);
        suburIndex !== -1 &&
          setFieldValue(
            "suburb",
            add.address_components[suburIndex].short_name
          );
        postalCodeIndex !== -1 &&
          setFieldValue(
            "postCode",
            add.address_components[postalCodeIndex].short_name
          );
      })
      .catch(error => console.error("Error", error));
  };
  
  
  const { 
    touchedUnitNumber, 
    touchedStreetNumber, 
    touchedSuburb, 
    touchedStreet, 
    touchedState, 
    touchedPostCode 
  } = getAddressTouchedValues(touched);
  const { 
    errorUnitNumber, 
    errorStreetNumber, 
    errorSuburb, 
    errorStreet, 
    errorState, 
    errorPostCode 
  } = getAddressErrorValues(errors);
  // fields must be in the same index for both arrays
  const addressTouchedList = [touchedUnitNumber, touchedStreetNumber, touchedSuburb, touchedStreet, touchedState, touchedPostCode];
  const addressErrorList = [errorUnitNumber, errorStreetNumber, errorSuburb, errorStreet, errorState, errorPostCode];
  useOpenAddressOnErrors(addressErrorList, addressTouchedList, manual, toggleManual);

  return (
    <Wrapper>
      <FieldWell>
        <PaddingTopMobile $padding="30" />
        <PaperWidthRow>
          <PaddingLeftCol xl={12}>
            <PlacesAutocomplete
              value={address}
              onChange={handleChangeLocal}
              onSelect={handleSelect}
              googleCallbackName="initMap"
              searchOptions={{
                componentRestrictions: {
                  country: "au"
                }
              }}
            >
              {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading
                }) => (
                <div className="address-wrapper">
                  <Input
                    {...getInputProps({
                      title: "Your current address",
                      placeholder: "Type your address",
                      suffix: [
                        <button
                          onClick={() => toggleManual(!manual)}
                          className="Address-ManualButton noselect"
                          type="button"
                          key="toggleManual"
                          id="addressManualButton"
                        >
                          <span>Manual</span>
                        </button>
                      ]
                    })}
                    value={address}
                  />
                  {(suggestions || []).length > 0 && (
                    <div className="pac-container pac-logo">
                      {suggestions.map((suggestion, index) => {
                        return (
                          <div
                            {...getSuggestionItemProps(suggestion)}
                            className="pac-item"
                            key={`suggestion${index}`}
                          >
                            <span className="pac-icon pac-icon-marker"></span>
                            <span className="pac-item-query">
                          <span className="pac-matched">
                            {(address || "").split(",")[0]}
                          </span>
                        </span>
                            <span>{suggestion.description}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </PlacesAutocomplete>
          </PaddingLeftCol>
        </PaperWidthRow>
        <ExpandedAddressDetails
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          handleBlur={handleBlur}
          isOpen={manual}
        />
        <PaddingTopMobile $padding="14" />
      </FieldWell>
    </Wrapper>
  );
}
